import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { useEditorChoice, type EditorChoice, type EditorScreen } from './editorPreference';

/**
 * Classic / New, in the editor's header. Switching keeps the page and swaps the editor; anything
 * unsaved is lost, so it asks first when `confirmLoss` says there is something to lose.
 */
export default function EditorSwitch({ screen, confirmLoss = false }: { screen: EditorScreen; confirmLoss?: boolean }) {
  const [choice, setChoice] = useEditorChoice(screen);
  const [pending, setPending] = React.useState<EditorChoice | null>(null);

  const request = (next: EditorChoice | null) => {
    if (!next || next === choice) return;
    if (confirmLoss) setPending(next);
    else setChoice(next);
  };

  return (
    <>
      <Tooltip title='Which editor to use. Remembered in this browser.'>
        <ToggleButtonGroup
          exclusive
          size='small'
          value={choice}
          onChange={(_event, next) => request(next)}
          aria-label='Editor version'
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, textTransform: 'none', fontSize: '0.75rem' } }}
        >
          <ToggleButton value='classic'>Classic</ToggleButton>
          <ToggleButton value='stack'>New editor</ToggleButton>
        </ToggleButtonGroup>
      </Tooltip>
      <Dialog open={pending !== null} onClose={() => setPending(null)}>
        <DialogTitle>Switch editors?</DialogTitle>
        <DialogContent>
          <DialogContentText>Changes you have not published stay behind in this editor and are lost.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Stay here</Button>
          <Button
            variant='contained'
            onClick={() => {
              if (pending) setChoice(pending);
              setPending(null);
            }}
          >
            Switch and discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
